import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, TextField, Typography, CircularProgress, IconButton, Tooltip,
  Select, MenuItem, Alert,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { Progress } from '../../../../components/tailwind-component/ui/progress';
import useWizardStepHelp from '../guide/useWizardStepHelp';
import { LINK_IN_BIO_GUIDE } from '../guide/guideContent';
import { StorefrontImageUpload } from '../../../../components/storefront-image-upload/storefront-image-upload';
import { BioService, BIO_THEMES, BUTTON_SHAPES } from '../../../../core/services/bio.service';
import { BioRender, socialIcon, SOCIAL_PLATFORMS } from './BioRender';
import { socialUrlError } from './bioValidation';
import { BASE_URL_UI } from '../../../../config/config';
import {
  BIO_STEPS,
  BioDraft,
  bioReviewRows,
  bioStepError,
  bioStepIsFilled,
  canFinishBio,
  cleanSocialLinks,
  emptyBioDraft,
  regeneratedBioSlug,
  slugify,
  suggestHeadline,
} from './bioSetup';

/**
 * First-run Link-in-Bio setup, one question at a time.
 *
 * Replaces the single long profile form a creator used to meet on their very
 * first visit — see the note at the top of bioSetup.ts for why. The editor
 * below still renders the full form once the page exists, where auto-save and
 * the complete set of controls belong.
 *
 * Mobile counterpart: BioSetupWizard.tsx in clanconnect_mobile.
 */

const BASE_HOST = (BASE_URL_UI || '').replace(/^https?:\/\//, '');
const DEFAULT_BG = BIO_THEMES.light.bg;

interface Props {
  influencerId: number;
  accountId: number;
  userId: number;
  brandId?: number | null;
  displayNameHint?: string;
  onCreated: () => void;
}

export const BioWizard: React.FC<Props> = ({
  influencerId,
  accountId,
  userId,
  brandId,
  displayNameHint,
  onCreated,
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  // ⓘ next to "Step X of 4" — the same step help as the Zap setup.
  const { helpButton, helpOverlay } = useWizardStepHelp(LINK_IN_BIO_GUIDE, stepIndex, 'bio_step_help_seen');
  const [draft, setDraft] = useState<BioDraft>(() => emptyBioDraft({ displayName: displayNameHint }));
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [showError, setShowError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);
  const [otherRows, setOtherRows] = useState<Set<number>>(new Set());
  // Synchronous, unlike state — a second click lands before a re-render and
  // would otherwise create the page twice.
  const savingRef = useRef(false);
  const topRef = useRef<HTMLDivElement | null>(null);

  const step = BIO_STEPS[stepIndex];
  const isLast = stepIndex === BIO_STEPS.length - 1;
  const error = bioStepError(step.id, draft, slugAvailable);
  const uploadPath = `accounts/${accountId}/influencer/${influencerId}/bio`;

  const set = (patch: Partial<BioDraft>) => setDraft((d) => ({ ...d, ...patch }));

  // The About line is written for them when they reach that step, so the box is
  // never an empty stare. Only once, and never over their own words.
  const headlineSeeded = useRef(false);
  useEffect(() => {
    if (step.id !== 'about' || headlineSeeded.current || draft.headline) return;
    headlineSeeded.current = true;
    set({ headline: suggestHeadline(draft.display_name || displayNameHint) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id]);

  // Next lands at the top of the new question, not wherever the last was scrolled to.
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [stepIndex]);

  // Availability, debounced. Nothing to check below 3 characters.
  useEffect(() => {
    const s = draft.slug;
    if (!s || s.length < 3) {
      setSlugAvailable(null);
      return;
    }
    setSlugChecking(true);
    const t = setTimeout(() => {
      BioService.checkSlug(s, influencerId, accountId)
        .then((res: any) => setSlugAvailable(res?.data?.available ?? null))
        .catch(() => setSlugAvailable(null))
        .finally(() => setSlugChecking(false));
    }, 400);
    return () => {
      clearTimeout(t);
      setSlugChecking(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.slug, influencerId, accountId]);

  // Typing a name shapes the link too, until they edit the link themselves.
  const handleName = (value: string) => {
    setShowError(false);
    setDraft((d) => ({
      ...d,
      display_name: value,
      slug: slugTouched ? d.slug : slugify(value),
    }));
  };

  // ---- social rows (same behaviour as the editor's form) ----
  const matchPredefined = (platform: string) =>
    SOCIAL_PLATFORMS.find((p: string) => p.toLowerCase() === (platform || '').toLowerCase().trim());
  const isOtherRow = (i: number, platform: string) =>
    otherRows.has(i) || (!!platform && !matchPredefined(platform));
  const setSocial = (i: number, key: 'platform' | 'url', value: string) => {
    setShowError(false);
    setDraft((d) => {
      const next = [...d.social_links];
      next[i] = { ...next[i], [key]: value };
      return { ...d, social_links: next };
    });
  };
  const addSocial = () => set({ social_links: [...draft.social_links, { platform: '', url: '' }] });
  const removeSocial = (i: number) => {
    setShowError(false);
    setDraft((d) => ({ ...d, social_links: d.social_links.filter((_, idx) => idx !== i) }));
    setOtherRows((prev) => {
      const next = new Set<number>();
      prev.forEach((idx) => {
        if (idx < i) next.add(idx);
        else if (idx > i) next.add(idx - 1);
      });
      return next;
    });
  };
  const handlePlatformSelect = (i: number, val: string) => {
    if (val === 'Other') {
      setOtherRows((prev) => new Set(prev).add(i));
      setSocial(i, 'platform', '');
    } else {
      setOtherRows((prev) => {
        const n = new Set(prev);
        n.delete(i);
        return n;
      });
      setSocial(i, 'platform', val);
    }
  };

  const handleNext = () => {
    if (error) {
      setShowError(true);
      return;
    }
    setShowError(false);
    setStepIndex((i) => Math.min(i + 1, BIO_STEPS.length - 1));
  };

  const handleBack = () => {
    setShowError(false);
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const handleCreate = async () => {
    if (savingRef.current) return;
    if (!canFinishBio(draft, slugAvailable)) {
      setShowError(true);
      setStepIndex(0);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setAlert(null);
    try {
      const res: any = await BioService.setupBioPage({
        ...draft,
        // Half-filled rows are dropped rather than saved as dead links.
        social_links: cleanSocialLinks(draft.social_links),
        influencer_id: influencerId,
        account_id: accountId,
        user_id: userId,
        brand_id: brandId || null,
      });
      if (res?.status === 1 && res?.data?.rows?.length) {
        onCreated();
      } else {
        setAlert(res?.info || 'Failed to create your page');
        savingRef.current = false;
      }
    } catch {
      setAlert('Something went wrong');
      savingRef.current = false;
    } finally {
      setSaving(false);
    }
  };

  const slugStatus = useMemo(() => {
    if (!draft.slug || draft.slug.length < 3) return null;
    if (slugChecking) return { ok: null as boolean | null, text: 'Checking…' };
    if (slugAvailable === true) return { ok: true, text: 'This link is yours' };
    if (slugAvailable === false) return { ok: false, text: 'Taken — try another' };
    return null;
  }, [draft.slug, slugChecking, slugAvailable]);

  const nextLabel = step.optional && !bioStepIsFilled(step.id, draft, DEFAULT_BG) ? 'Skip' : 'Next';

  // The real page renderer, fed the draft — so the preview cannot drift from
  // what visitors actually get.
  const previewPage = {
    ...draft,
    social_links: cleanSocialLinks(draft.social_links),
    display_name: draft.display_name || 'Your name',
  };

  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'flex-start' }} ref={topRef}>
      {helpOverlay}
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: 720 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' }}>
                Step {stepIndex + 1} of {BIO_STEPS.length}
              </Typography>
              {helpButton}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {BIO_STEPS.map((s, i) => (
                <Typography
                  key={s.id}
                  sx={{
                    fontSize: 12,
                    fontWeight: i === stepIndex ? 700 : 500,
                    color: i === stepIndex ? '#7c3aed' : i < stepIndex ? '#111' : '#c4c4cc',
                  }}
                >
                  {s.stepLabel}
                  {i < BIO_STEPS.length - 1 ? '  ›' : ''}
                </Typography>
              ))}
            </Box>
          </Box>
          <Progress value={((stepIndex + 1) / BIO_STEPS.length) * 100} />
        </Box>

        {!!alert && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAlert(null)}>{alert}</Alert>}

        <Box sx={{ border: '1px solid #ececf0', borderRadius: '14px', bgcolor: '#fff', p: { xs: 2, md: 3 } }}>
          <Typography sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 700, color: '#111' }}>{step.title}</Typography>
          <Typography sx={{ fontSize: 14, color: '#6b7280', mt: 0.5, mb: 2.5 }}>{step.subtitle}</Typography>

          {step.id === 'name' && (
            <Box>
              <TextField
                fullWidth
                autoFocus
                label="Page name"
                value={draft.display_name}
                onChange={(e) => handleName(e.target.value)}
                error={showError && !draft.display_name.trim()}
                inputProps={{ maxLength: 60 }}
              />

              <Box sx={{ mt: 2.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mb: 0.75 }}>
                  Your page link
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 14, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {BASE_HOST}/bio/
                  </Typography>
                  <TextField
                    size="small"
                    sx={{ flex: 1 }}
                    value={draft.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setShowError(false);
                      set({ slug: slugify(e.target.value) });
                    }}
                    error={showError && slugAvailable === false}
                  />
                  <Tooltip title="Suggest another link">
                    <IconButton
                      onClick={() => {
                        setSlugTouched(true);
                        set({ slug: regeneratedBioSlug(displayNameHint || draft.display_name) });
                      }}
                    >
                      <AutorenewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {!!slugStatus && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                    {slugStatus.ok === true && <CheckCircleIcon sx={{ fontSize: 14, color: '#16a34a' }} />}
                    {slugStatus.ok === false && <ErrorOutlineIcon sx={{ fontSize: 14, color: '#dc2626' }} />}
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: slugStatus.ok === true ? '#16a34a' : slugStatus.ok === false ? '#dc2626' : '#6b7280',
                      }}
                    >
                      {slugStatus.text}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {step.id === 'look' && (
            <Box>
              <StorefrontImageUpload
                label="Your photo"
                hint="Square works best — around 500 × 500"
                value={draft.avatar_url}
                uploadPath={uploadPath}
                previewVariant="logo"
                onChange={(m) =>
                  set({
                    avatar_url: m.url,
                    avatar_file_name: m.file_name,
                    avatar_file_type: m.file_type,
                    avatar_file_path: m.file_path,
                    avatar_storage_size: m.storage_size,
                  })
                }
                onRemove={() =>
                  set({
                    avatar_url: '',
                    avatar_file_name: '',
                    avatar_file_type: '',
                    avatar_file_path: '',
                    avatar_storage_size: '',
                  })
                }
              />

              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mt: 3, mb: 1 }}>Colour</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {Object.entries(BIO_THEMES).map(([key, t]: [string, any]) => {
                  const active = draft.theme.bg === t.bg;
                  return (
                    <Box
                      key={key}
                      role="button"
                      onClick={() => set({ theme: { ...t, button_shape: draft.theme.button_shape } })}
                      sx={{
                        width: 88,
                        cursor: 'pointer',
                        border: active ? '2px solid #7c3aed' : '2px solid transparent',
                        borderRadius: '10px',
                        p: '3px',
                      }}
                    >
                      <Box
                        sx={{
                          height: 66,
                          bgcolor: t.bg,
                          border: '1px solid #f3f4f6',
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: t.accent }} />
                        <Box sx={{ width: 30, height: 3, borderRadius: 2, bgcolor: t.text, opacity: 0.7 }} />
                        <Box sx={{ width: 36, height: 8, borderRadius: 1, bgcolor: t.accent }} />
                      </Box>
                      <Typography sx={{ fontSize: 11, color: '#6b7280', textAlign: 'center', mt: 0.5 }}>
                        {t.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mt: 3, mb: 1 }}>
                Button style
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {Object.keys(BUTTON_SHAPES).map((shape) => (
                  <Box
                    key={shape}
                    role="button"
                    onClick={() => set({ theme: { ...draft.theme, button_shape: shape } })}
                    sx={{
                      flex: 1,
                      maxWidth: 140,
                      cursor: 'pointer',
                      border: draft.theme.button_shape === shape ? '2px solid #7c3aed' : '2px solid #ececf0',
                      borderRadius: '10px',
                      p: 1,
                    }}
                  >
                    <Box
                      sx={{
                        height: 32,
                        bgcolor: draft.theme.accent,
                        borderRadius: shape === 'pill' ? '999px' : shape === 'square' ? '2px' : '10px',
                        mb: 0.5,
                      }}
                    />
                    <Typography sx={{ fontSize: 11, color: '#6b7280', textAlign: 'center', textTransform: 'capitalize' }}>
                      {shape}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {step.id === 'about' && (
            <Box>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="About you"
                value={draft.headline}
                onChange={(e) => set({ headline: e.target.value })}
                inputProps={{ maxLength: 200 }}
                helperText={`${draft.headline.length}/200`}
              />

              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mt: 2.5, mb: 1 }}>
                Where else can people find you?
              </Typography>
              {draft.social_links.length === 0 && (
                <Typography sx={{ fontSize: 13, color: '#9ca3af', mb: 1.5 }}>
                  Nothing yet — add Instagram, YouTube, anywhere.
                </Typography>
              )}

              {draft.social_links.map((s, i) => {
                const predefined = matchPredefined(s.platform);
                const other = isOtherRow(i, s.platform);
                const urlError = socialUrlError(s.platform, s.url, predefined);
                return (
                  <Box
                    key={i}
                    sx={{ border: '1px solid #ececf0', borderRadius: '10px', p: 1.5, mb: 1.5 }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      {other ? (
                        <TextField
                          size="small"
                          placeholder="Platform name"
                          value={s.platform}
                          onChange={(e) => setSocial(i, 'platform', e.target.value)}
                          sx={{ width: 180 }}
                          InputProps={{
                            startAdornment: (
                              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                {socialIcon(s.platform)}
                              </Box>
                            ),
                          }}
                        />
                      ) : (
                        <Select
                          size="small"
                          displayEmpty
                          value={predefined || ''}
                          onChange={(e) => handlePlatformSelect(i, e.target.value as string)}
                          sx={{ width: 180 }}
                          renderValue={(v) =>
                            v ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {socialIcon(v as string)} {v as string}
                              </Box>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>Select platform</span>
                            )
                          }
                        >
                          {[...SOCIAL_PLATFORMS, 'Other'].map((p: string) => (
                            <MenuItem key={p} value={p}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {socialIcon(p === 'Other' ? '' : p)} {p}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      )}

                      <TextField
                        size="small"
                        sx={{ flex: 1 }}
                        placeholder="https://…"
                        value={s.url}
                        onChange={(e) => setSocial(i, 'url', e.target.value)}
                        error={!!urlError}
                        helperText={urlError || ' '}
                      />

                      <IconButton onClick={() => removeSocial(i)} sx={{ color: '#dc2626' }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}

              <Button variant="outlined" startIcon={<AddIcon />} onClick={addSocial}>
                Add social link
              </Button>
            </Box>
          )}

          {step.id === 'review' && (
            <Box>
              {bioReviewRows(draft).map((row) => (
                <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography sx={{ fontSize: 13, color: '#6b7280' }}>{row.label}</Typography>
                  <Typography
                    sx={{ fontSize: 13, color: row.missing ? '#9ca3af' : '#111', fontWeight: row.missing ? 400 : 600 }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              ))}
              <Typography sx={{ fontSize: 12, color: '#9ca3af', mt: 1.5 }}>
                Anything left for later — and your links, buttons and blocks — come next, once your page is live.
              </Typography>
            </Box>
          )}

          {showError && !!error && (
            <Typography sx={{ fontSize: 13, color: '#dc2626', mt: 1.5 }}>{error}</Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 3,
            pt: 2,
            borderTop: '1px solid #ececf0',
          }}
        >
          <Button variant="outlined" onClick={handleBack} disabled={stepIndex === 0} startIcon={<ChevronLeftIcon />}>
            Back
          </Button>
          {isLast ? (
            <Button variant="contained" size="large" onClick={handleCreate} disabled={saving}>
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Go live'}
            </Button>
          ) : (
            <Button variant="contained" size="large" onClick={handleNext} endIcon={<ChevronRightIcon />}>
              {nextLabel}
            </Button>
          )}
        </Box>
      </Box>

      {/* The page itself, live, beside every step. */}
      <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0, position: { md: 'sticky' }, top: 16 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', mb: 1, letterSpacing: '.5px' }}>
          LIVE PREVIEW
        </Typography>
        <Box
          sx={{
            border: '1px solid #ececf0',
            borderRadius: '14px',
            overflow: 'hidden',
            height: 520,
            overflowY: 'auto',
          }}
        >
          <BioRender page={previewPage} blocks={[]} hrefFor={() => ''} />
        </Box>
        <Typography sx={{ fontSize: 12, color: '#9ca3af', mt: 1, wordBreak: 'break-all' }}>
          {BASE_HOST}/bio/{draft.slug || '…'}
        </Typography>
      </Box>
    </Box>
  );
};

export default BioWizard;
